<template>
  <el-card class="box-card full-screen">
    <div ref="pageHead" class="table-page-head">
      <el-row type="flex" justify="space-between" align="middle">
        <el-col>
          <span class="page-title">{{ $t("escrowAccount") }}</span>
        </el-col>
      </el-row>
    </div>

    <!-- Two sections side by side -->
    <div
      class="data-table"
      :style="{
        top: queryTableHeight,
        height: `calc(100% - ${queryTableHeight} - 70px)`,
      }"
    >
      <el-row :gutter="16" style="height: 100%">
        <el-col :span="12" style="height: 100%">
          <div class="section-card">
            <el-table
              :data="tableData.records"
              v-loading="isLoading"
              element-loading-background="rgba(255,255,255,0.4)"
              size="mini"
              height="calc(100% - 40px)"
            >
              <el-table-column
                prop="bankName"
                :label="$t('bankName')"
                min-width="130"
              >
                <template slot-scope="scope">{{ scope.row.bankName }}</template>
              </el-table-column>
              <el-table-column
                prop="accountAmount"
                :label="$t('accountAmount')"
                min-width="140"
              >
                <template slot-scope="scope">
                  <span class="franc">₣</span
                  >{{ scope.row.accountAmount.formatMoney(0) }}
                </template>
              </el-table-column>
              <el-table-column prop="status" :label="$t('status')" width="80">
                <template slot-scope="scope">
                  <el-tag
                    v-if="scope.row.status == 0"
                    type="success"
                    size="mini"
                    >{{ $t("normal") }}</el-tag
                  >
                  <el-tag
                    v-if="scope.row.status == 1"
                    type="danger"
                    size="mini"
                    >{{ $t("lock") }}</el-tag
                  >
                </template>
              </el-table-column>
              <el-table-column fixed="right" :label="$t('extend')" width="160">
                <template slot-scope="scope">
                  <el-button
                    size="mini"
                    type="primary"
                    @click="editBank(scope.row)"
                    >{{ $t("feed") }}</el-button
                  >
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-col>

        <el-col :span="12" style="height: 100%">
          <div class="section-card">
            <div class="section-header">
              <span>{{ $t("feedHistory") }}</span>
              <el-select
                v-model="applyQueryStatus"
                size="mini"
                :placeholder="$t('status')"
                style="width: 120px"
                clearable
                @change="getEscrowInfoPage"
              >
                <el-option :value="0" :label="$t('checkPending')"></el-option>
                <el-option :value="1" :label="$t('getApproved')"></el-option>
                <el-option :value="3" :label="$t('notApproved')"></el-option>
              </el-select>
            </div>
            <el-table
              :data="applyTableData.records"
              v-loading="isApplyLoading"
              element-loading-background="rgba(255,255,255,0.4)"
              size="mini"
              height="calc(100% - 80px)"
            >
              <el-table-column
                prop="applyAmount"
                :label="$t('amount')"
                min-width="130"
              >
                <template slot-scope="scope">
                  <span class="franc">₣</span
                  >{{ scope.row.applyAmount.formatMoney(0) }}
                </template>
              </el-table-column>
              <el-table-column prop="status" :label="$t('status')" width="100">
                <template slot-scope="scope">
                  <el-tag
                    v-if="scope.row.status == 0"
                    type="warning"
                    size="mini"
                    >{{ $t("checkPending") }}</el-tag
                  >
                  <el-tag
                    v-if="scope.row.status == 1"
                    type="success"
                    size="mini"
                    >{{ $t("getApproved") }}</el-tag
                  >
                  <el-tag
                    v-if="scope.row.status == 3"
                    type="danger"
                    size="mini"
                    >{{ $t("notApproved") }}</el-tag
                  >
                </template>
              </el-table-column>
              <el-table-column
                prop="applyRemark"
                :label="$t('remark')"
                min-width="120"
              >
                <template slot-scope="scope">
                  <div class="overstep-hidden">{{ scope.row.applyRemark }}</div>
                </template>
              </el-table-column>
              <el-table-column
                prop="applyTime"
                :label="$t('creationTime')"
                min-width="150"
              >
                <template slot-scope="scope">
                  {{ $manba(scope.row.applyTime).format("ymdhms") }}
                </template>
              </el-table-column>
            </el-table>
            <el-pagination
              small
              background
              layout="total,prev,pager,next"
              :total="applyTableData.total || 0"
              :page-size="applyPageSize"
              :current-page="applyCurrentPage"
              @current-change="setApplyCurrentChange"
              style="margin-top: 8px; text-align: right"
            />
          </div>
        </el-col>
      </el-row>
    </div>

    <el-drawer
      :title="$t('feed') + ' - ' + (form.bankName || '')"
      :visible.sync="editBankdrawer"
      direction="rtl"
      :modal="false"
      :destroy-on-close="true"
      :wrapperClosable="false"
      :size="480"
    >
      <div class="drawer-content">
        <div class="bank-summary">
          <div class="summary-row">
            <span class="summary-label">{{ $t("bankName") }}</span>
            <span class="summary-value">{{ form.bankName }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">{{ $t("bankAddress") }}</span>
            <span class="summary-value">{{ form.bankAddress }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">{{ $t("accountAmount") }}</span>
            <span class="summary-value amount">
              ₣
              {{ form.accountAmount ? form.accountAmount.formatMoney(0) : "0" }}
            </span>
          </div>
        </div>

        <el-divider content-position="left">{{ $t("newAmount") }}</el-divider>

        <el-form
          :model="bankForm"
          :rules="rules"
          ref="bankForm"
          label-position="top"
        >
          <el-form-item :label="$t('newAmount')" prop="applyAmount">
            <el-input
              size="medium"
              v-model="bankForm.applyAmount"
              type="number"
              :placeholder="$t('newAmount')"
            >
              <template slot="prepend">₣</template>
            </el-input>
          </el-form-item>
          <el-form-item :label="$t('remark')" prop="applyRemark">
            <el-input
              size="medium"
              type="textarea"
              :rows="3"
              v-model="bankForm.applyRemark"
              :placeholder="$t('remark')"
            ></el-input>
          </el-form-item>
          <el-form-item>
            <el-button
              type="primary"
              size="medium"
              style="width: 100%"
              :loading="isloading"
              @click="updateSubmit('bankForm')"
              >{{ $t("submit") }}</el-button
            >
          </el-form-item>
        </el-form>
      </div>
    </el-drawer>
  </el-card>
</template>

<script>
import bank from "@/api/bank";
export default {
  name: "bankEscrowAccount",
  data() {
    return {
      queryTableHeight: String,
      tableData: {},
      applyTableData: {},
      pageSize: 20,
      currentPage: 1,
      applyPageSize: 20,
      applyCurrentPage: 1,
      applyQueryStatus: null,
      isLoading: false,
      isApplyLoading: false,
      isloading: false,
      editBankdrawer: false,
      bankForm: {},
      form: {},
    };
  },
  computed: {
    rules() {
      return {
        applyAmount: [
          {
            required: true,
            message: this.$t("requiredFields"),
            trigger: "blur",
          },
        ],
      };
    },
    loginInfo() {
      return this.$store.state.globalParameter.loginInfo;
    },
  },
  methods: {
    getList() {
      this.isLoading = true;
      bank.getEscrowAccountPage({
        param: { current: this.currentPage, size: this.pageSize },
        success: (res) => {
          this.tableData = res.data || {};
          this.isLoading = false;
        },
      });
    },

    getEscrowInfoPage() {
      this.isApplyLoading = true;
      bank.getEscrowInfoPage({
        param: {
          current: this.applyCurrentPage,
          size: this.applyPageSize,
          status: this.applyQueryStatus,
        },
        success: (res) => {
          this.applyTableData = res.data || {};
          this.isApplyLoading = false;
        },
      });
    },

    setPageSzie(val) {
      this.pageSize = val;
      this.getList();
    },
    setCurrentChange(val) {
      this.currentPage = val;
      this.getList();
    },
    setApplyCurrentChange(val) {
      this.applyCurrentPage = val;
      this.getEscrowInfoPage();
    },

    editBank(data) {
      this.form = data || {};
      this.bankForm = {};
      this.editBankdrawer = true;
    },

    resetForm() {
      this.bankForm.applyAmount = "";
      this.bankForm.applyRemark = "";
    },

    updateSubmit(formName) {
      this.$refs[formName].validate((valid) => {
        if (!valid) return;
        this.isloading = true;
        bank.feedEscrowAccount({
          param: this[formName],
          success: (res) => {
            if (res.code == 200) {
              this.$message({
                type: "success",
                message: this.$t("operateSuccessfully"),
              });
              this.getList();
              this.getEscrowInfoPage();
              this.editBankdrawer = false;
            } else {
              this.$message({ type: "warning", message: this.$t(res.msg) });
            }
            this.isloading = false;
            this.resetForm();
          },
        });
      });
    },

    onLock(row, status) {
      this.$confirm(this.$t("confirmOperation"), this.$t("warning"), {
        confirmButtonText: this.$t("confirm"),
        cancelButtonText: this.$t("cancel"),
        type: "warning",
      })
        .then(() => {
          bank.updateBankStatus({
            param: { id: row.id, status },
            success: (res) => {
              if (res.code == 200) {
                this.$message({
                  type: "success",
                  message: this.$t("operateSuccessfully"),
                });
                this.getList();
              } else {
                this.$message({ type: "warning", message: this.$t(res.msg) });
              }
            },
          });
        })
        .catch(() => {});
    },
  },

  mounted() {
    let queryTableHeight = this.$refs.pageHead.offsetHeight + 15;
    this.queryTableHeight = queryTableHeight + "px";
    this.getList();
    this.getEscrowInfoPage();
  },
};
</script>

<style lang="less" scoped>
.section-card {
  height: 100%;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  overflow: hidden;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    background: #f5f7fa;
    border-bottom: 1px solid #ebeef5;
    font-weight: 600;
    font-size: 14px;
    color: #303133;
  }
}

.drawer-content {
  padding: 20px;

  .bank-summary {
    background: #f5f7fa;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;

    .summary-row {
      display: flex;
      justify-content: space-between;
      padding: 6px 0;
      border-bottom: 1px solid #ebeef5;
      &:last-child {
        border-bottom: none;
      }

      .summary-label {
        color: #909399;
        font-size: 13px;
      }
      .summary-value {
        font-size: 13px;
        font-weight: 600;
        color: #303133;
        &.amount {
          color: #409eff;
          font-size: 15px;
        }
      }
    }
  }
}

.page-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
</style>
